import { Entity } from '../entity.js';
import { EFFECT_TYPES } from '../combat/statusEffects.js';
import {
  isInAbilityCircle,
  isInAbilityCone,
  isWithinAbilityRange,
  scaleLolDistance
} from '../combat/abilityArea.js';

export const SETT_GRIT_FROM_DAMAGE_RATIO = 0.4;
export const SETT_GRIT_MAX_RATIO = 0.5;
export const SETT_ROUGH_HP_HEAL_RATIO = 0.02;
export const SETT_GRIT_DECAY_RATIO = 0.05;

export function createSett(id = 'sett-1', team = 'blue', x = 300, y = 1300) {
  const sett = new Entity({
    id,
    type: 'champion',
    team,
    x,
    y,
    maxHp: 620,
    maxMp: 0,
    ad: 61,
    ap: 0,
    armor: 34,
    mr: 32,
    ms: 340,
    attackRange: 125,
    attackSpeed: 0.625,
    name: 'Sett',
    radius: 22
  });
  sett.qEmpowered = false;
  sett.grit = 0;
  sett.onDamageCallback = ({ effectiveDamage }) => {
    applySettPitGrit(sett, effectiveDamage);
  };
  return sett;
}

/** Pit Grit: store a portion of damage taken as grit (capped). */
export function applySettPitGrit(sett, effectiveDamage) {
  if (!sett || sett.isDead || !(effectiveDamage > 0)) return 0;
  const maxGrit = sett.maxHp * SETT_GRIT_MAX_RATIO;
  const gained = effectiveDamage * SETT_GRIT_FROM_DAMAGE_RATIO;
  const before = sett.grit || 0;
  sett.grit = Math.min(maxGrit, before + gained);
  return sett.grit - before;
}

/** Rough HP: grit slowly heals Sett while decaying. */
export function tickSettRoughHp(sett, dt = 0) {
  if (!sett || sett.isDead || !(sett.grit > 0) || !(dt > 0)) return;
  const heal = Math.min(sett.grit * SETT_ROUGH_HP_HEAL_RATIO * dt, sett.maxHp - sett.hp);
  if (heal > 0) sett.hp += heal;
  sett.grit = Math.max(0, sett.grit - sett.maxHp * SETT_GRIT_DECAY_RATIO * dt);
}

export function castSettQ(caster) {
  if (!caster || caster.isDead) return false;
  caster.qEmpowered = true;
  caster.ms += 50;
  return true;
}

export function castSettW(caster, targetX, targetY, entities = []) {
  if (!caster || caster.isDead) return false;
  const angle = Math.atan2(targetY - caster.y, targetX - caster.x);
  const damage = 50 + caster.ad * 0.6;
  const grit = caster.grit || 0;
  caster.shieldHp = (caster.shieldHp || 0) + 95 + caster.maxHp * 0.1 + grit;
  caster.grit = 0;
  for (const e of entities) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (!isInAbilityCone(e, caster.x, caster.y, angle, 720, 0.7, caster)) continue;
    e.takeDamage(damage, caster, 'true');
  }
  return true;
}

export function castSettE(caster, entities = []) {
  if (!caster || caster.isDead) return false;
  for (const e of entities) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (!isInAbilityCircle(e, caster.x, caster.y, 450, caster)) continue;
    e.takeDamage(40 + caster.ad * 0.4, caster, 'physical');
    e.statusManager?.addEffect?.(EFFECT_TYPES.STUN, 0.75, 0, e.tenacity || 0);
  }
  return true;
}

export function castSettR(caster, target) {
  if (!caster || caster.isDead || !target || target.isDead) return false;
  if (!isWithinAbilityRange(caster, target, 400, caster)) return false;
  const damage = 150 + caster.ad * 1.0;
  target.takeDamage(damage, caster, 'physical');
  const dx = target.x - caster.x;
  const dy = target.y - caster.y;
  const dist = Math.hypot(dx, dy) || 1;
  const throwDist = scaleLolDistance(180, caster);
  target.x = caster.x - (dx / dist) * throwDist;
  target.y = caster.y - (dy / dist) * throwDist;
  if (Number.isFinite(target.z)) target.z = target.y;
  return true;
}
